import { useState, useEffect } from 'react';
import { programImageAPI, uploadAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Plus, Pencil, Trash2, Upload, Loader2, Image as ImageIcon,
  Palette, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_PROGRAM_IMAGE = {
  program_id: '',
  name: '',
  description: '',
  image_url: '',
  color: '#4472C4',
  is_active: true,
  sort_order: 0
};

export function ProgramImagesTab() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_PROGRAM_IMAGE);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      setLoading(true);
      const res = await programImageAPI.getAll();
      setImages(res.data || []);
    } catch (error) {
      console.error('Failed to load program images:', error);
      toast.error('Gagal memuat gambar program');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingImage(null);
    setFormData({ ...DEFAULT_PROGRAM_IMAGE, sort_order: images.length });
    setDialogOpen(true);
  };

  const openEditDialog = (img) => {
    setEditingImage(img);
    setFormData({
      program_id: img.program_id || '',
      name: img.name || '',
      description: img.description || '',
      image_url: img.image_url || '',
      color: img.color || '#4472C4',
      is_active: img.is_active !== false,
      sort_order: img.sort_order || 0
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Hanya file gambar yang diizinkan');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const res = await uploadAPI.uploadFile(uploadFormData);
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Gambar berhasil diupload');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Gagal mengupload gambar');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.program_id.trim()) {
      toast.error('Program ID harus diisi');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Nama harus diisi');
      return;
    }

    try {
      setSaving(true);
      
      if (editingImage) {
        await programImageAPI.update(editingImage.id, formData);
        toast.success('Gambar program berhasil diperbarui');
      } else {
        await programImageAPI.create(formData);
        toast.success('Gambar program berhasil ditambahkan');
      }
      
      setDialogOpen(false);
      loadImages();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan gambar program');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus gambar program ini?')) return;
    
    try {
      await programImageAPI.delete(id);
      toast.success('Gambar program berhasil dihapus');
      loadImages();
    } catch (error) {
      toast.error('Gagal menghapus gambar program');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await programImageAPI.seedDefaults();
      toast.success(res.data.message);
      loadImages();
    } catch (error) {
      toast.error('Gagal membuat default program images');
    }
  };

  const toggleActive = async (img) => {
    try {
      await programImageAPI.update(img.id, { is_active: !img.is_active });
      toast.success(img.is_active ? 'Gambar dinonaktifkan' : 'Gambar diaktifkan');
      loadImages();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4472C4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Gambar Program</h2>
          <p className="text-sm text-slate-500">Kelola gambar untuk setiap program yang ditampilkan di landing page dan dashboard</p>
        </div>
        <div className="flex gap-2">
          {images.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Buat Default
            </Button>
          )}
          <Button onClick={openCreateDialog} className="bg-[#4472C4] hover:bg-[#3861a8]">
            <Plus className="w-4 h-4 mr-2" />
            Tambah Program
          </Button>
        </div>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">Belum ada gambar program</p>
            <Button onClick={handleSeedDefaults} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Buat Default Program Images
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <Card key={img.id} className={`relative overflow-hidden ${!img.is_active ? 'opacity-60' : ''}`}>
              {/* Image Preview */}
              <div 
                className="h-32 flex items-center justify-center"
                style={{ backgroundColor: img.color || '#4472C4' }}
              >
                {img.image_url ? (
                  <img 
                    src={img.image_url} 
                    alt={img.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-white text-center">
                    <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-xs opacity-75">Belum ada gambar</p>
                  </div>
                )}
              </div>
              
              <CardContent className="p-4">
                {/* Status Badge */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{img.program_id}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${img.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {img.is_active ? 'Aktif' : 'Nonaktif'}
                  </span>
                </div>

                {/* Info */}
                <h3 className="font-semibold text-slate-900">{img.name}</h3>
                <p className="text-xs text-slate-500 line-clamp-2 mt-1">{img.description}</p>
                
                {/* Color Preview */}
                <div className="flex items-center gap-2 mt-3">
                  <div 
                    className="w-5 h-5 rounded border border-slate-300"
                    style={{ backgroundColor: img.color }}
                  />
                  <span className="text-xs text-slate-400">{img.color}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openEditDialog(img)}
                      className="h-8 px-2"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(img.id)}
                      className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Switch 
                    checked={img.is_active} 
                    onCheckedChange={() => toggleActive(img)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingImage ? 'Edit Gambar Program' : 'Tambah Gambar Program Baru'}
            </DialogTitle>
            <DialogDescription>
              Gambar ini akan ditampilkan di landing page dan dashboard siswa
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Image Upload */}
            <div>
              <Label>Gambar Program</Label>
              <div className="mt-2 flex items-center gap-4">
                {formData.image_url ? (
                  <img 
                    src={formData.image_url} 
                    alt="Preview"
                    className="w-24 h-16 rounded object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div 
                    className="w-24 h-16 rounded flex items-center justify-center"
                    style={{ backgroundColor: formData.color }}
                  >
                    <ImageIcon className="w-8 h-8 text-white/50" />
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="hidden"
                    id="program-image-upload"
                  />
                  <label htmlFor="program-image-upload">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      disabled={uploading}
                      asChild
                    >
                      <span>
                        {uploading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploading ? 'Mengupload...' : 'Upload Gambar'}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
              <Input
                placeholder="Atau masukkan URL gambar..."
                value={formData.image_url}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                className="mt-2"
              />
            </div>

            {/* Program ID */}
            <div>
              <Label>Program ID *</Label>
              <Input
                placeholder="Contoh: UTBK-SNBT, CPNS, TNI, POLRI"
                value={formData.program_id}
                onChange={(e) => setFormData(prev => ({ ...prev, program_id: e.target.value.toUpperCase() }))}
                disabled={!!editingImage}
              />
              <p className="text-xs text-slate-400 mt-1">ID unik untuk program (tidak bisa diubah setelah dibuat)</p>
            </div>

            {/* Name */}
            <div>
              <Label>Nama Program *</Label>
              <Input
                placeholder="Contoh: UTBK-SNBT 2026"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            {/* Description */}
            <div>
              <Label>Deskripsi</Label>
              <Textarea
                placeholder="Deskripsi singkat program..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Color */}
            <div>
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Warna Tema
              </Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 rounded border cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1"
                  placeholder="#4472C4"
                />
              </div>
            </div>

            {/* Sort Order */}
            <div>
              <Label>Urutan Tampil</Label>
              <Input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Status Aktif</Label>
                <p className="text-xs text-slate-400">Tampilkan di landing page dan dashboard</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#4472C4] hover:bg-[#3861a8]"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingImage ? 'Simpan Perubahan' : 'Tambah Program'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProgramImagesTab;
